import { api } from '../../lib/api';
import { buildUploadedMedia, validateMediaFile } from '../../lib/publishing/mediaUpload';
import type {
  CaptionDraftsResult,
  CaptionQuestionsResult,
  CaptionRefineResult,
  ExternalPostSummary,
  LinkPreview,
  Post,
  PostAccountPlatforms,
  PostInput,
  PostMedia,
  PostPlatform,
  PostQueue,
  PostQueueSlot,
  PostSummary,
  PublishingAccount,
} from '../../types/publishing';
import type { ValidateIssue } from '../../lib/publishing/validateWarnings';
import type { PostStats } from '../../lib/publishing/postStats';
import type { WildcardContext } from '../../lib/publishing/captionWildcards';
import type {
  CaptionDraftRequest,
  CaptionRefineRequest,
} from '../../lib/publishing/captionDrafting';
import type { FanOutResultRow, FanOutSummary } from '../../lib/publishing/multiAccount';

// Publishing Engine — client API. Mirrors /api/publishing. Media upload is the
// one path that leaves this origin: the server only presigns, and the file itself
// is PUT straight to Zernio's storage (see uploadMedia).

export async function listPublishingAccounts(): Promise<PublishingAccount[]> {
  const { accounts } = await api<{ accounts: PublishingAccount[] }>('/api/publishing/accounts');
  return accounts;
}

// The platforms endpoint also ships the per-account caption-wildcard context, so
// the composer preview resolves the same tokens dispatch will (empty → blank).
export interface AccountPlatformsResponse extends PostAccountPlatforms {
  wildcard: WildcardContext;
}

// Post to Many's per-account preview: every selected account's wildcard
// context in ONE request (one roster read server-side). An unknown account comes
// back null; past the server's cap the request is refused (400) with a message.
export async function getWildcardContexts(
  accountIds: string[],
): Promise<Record<string, WildcardContext | null>> {
  const res = await api<{ contexts: Record<string, WildcardContext | null> }>(
    `/api/publishing/wildcard-contexts?accountIds=${accountIds.map(encodeURIComponent).join(',')}`,
  );
  return res.contexts;
}

// The Facebook link-card preview (server fetches the page through safeFetch).
// null = no card to show — the caller renders nothing.
export async function getLinkPreview(url: string): Promise<LinkPreview | null> {
  const res = await api<{ preview: LinkPreview | null }>(
    `/api/publishing/link-preview?url=${encodeURIComponent(url)}`,
  );
  return res.preview;
}

export async function getAccountPlatforms(accountId: string): Promise<AccountPlatformsResponse> {
  return api<AccountPlatformsResponse>(
    `/api/publishing/accounts/${encodeURIComponent(accountId)}/platforms`,
  );
}

// AI caption drafting. ⚠️ ON-DEMAND ONLY — this must only ever be called from an
// explicit click. Never wire it into a useQuery or a useEffect; the source-shape
// guard captionDraftOnDemandOnly.test.ts pins that.
export async function draftCaption(input: CaptionDraftRequest): Promise<CaptionDraftsResult> {
  return api<CaptionDraftsResult>('/api/publishing/draft-caption', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// The clarifying questions the model wants answered before writing. ⚠️ An empty
// list is a NORMAL answer, not an error — the caller then drafts directly.
export async function captionQuestions(
  input: CaptionDraftRequest,
): Promise<CaptionQuestionsResult> {
  return api<CaptionQuestionsResult>('/api/publishing/caption-questions', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// One revision of the caption the AM is looking at. ⚠️ Same on-demand rule as
// draftCaption — a click, never an effect.
export async function refineCaption(input: CaptionRefineRequest): Promise<CaptionRefineResult> {
  return api<CaptionRefineResult>('/api/publishing/refine-caption', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function listPosts(): Promise<PostSummary[]> {
  const { posts } = await api<{ posts: PostSummary[] }>('/api/publishing/posts');
  return posts;
}

/**
 * Selection-scoped posts for the calendar / drafts page — only the given
 * accounts (the server chunks the ids into ≤30 `in` batches). An empty selection
 * returns [] WITHOUT a request; callers should gate the query on a non-empty
 * selection anyway (React Query `enabled`).
 */
export async function listPostsForAccounts(accountIds: string[]): Promise<PostSummary[]> {
  if (accountIds.length === 0) return [];
  const q = encodeURIComponent(accountIds.join(','));
  const { posts } = await api<{ posts: PostSummary[] }>(`/api/publishing/posts?accountIds=${q}`);
  return posts;
}

export async function getPost(id: string): Promise<Post> {
  const { post } = await api<{ post: Post }>(`/api/publishing/posts/${encodeURIComponent(id)}`);
  return post;
}

export interface ExternalPostsResponse {
  posts: ExternalPostSummary[];
  /** A page cap stopped the fetch short of the full history — surfaced in the UI. */
  capped: boolean;
  failures: Array<{ platform: PostPlatform; message: string }>;
}

/**
 * Natively-published posts for ONE account, fetched live from Zernio.
 *
 * Scoped to a single account by design, not just for performance: these rows are
 * not mirrored locally, so a roster-wide call would be one Zernio round-trip per
 * connected platform per account on every calendar render. See the caching note
 * in PublishingCalendarPage.
 */
export async function listExternalPosts(accountId: string): Promise<ExternalPostsResponse> {
  return api<ExternalPostsResponse>(
    `/api/publishing/posts/external?accountId=${encodeURIComponent(accountId)}`,
  );
}

export interface PostStatsResponse {
  stats: PostStats | null;
  unavailable: 'gbp-unsupported' | 'not-found' | 'error' | null;
  message: string | null;
}

/**
 * Statistics for a post — ours or an external one (the endpoint accepts both id
 * kinds). `platforms` lets the server answer a Google-Business-only post without
 * a network call, and never with zeros.
 */
export async function getPostStats(
  postId: string,
  platforms: PostPlatform[],
): Promise<PostStatsResponse> {
  const q = platforms.length > 0 ? `?platforms=${encodeURIComponent(platforms.join(','))}` : '';
  return api<PostStatsResponse>(
    `/api/publishing/posts/${encodeURIComponent(postId)}/stats${q}`,
  );
}

export interface SavePostResult {
  post: Post;
  dispatchError: string | null;
}

export async function createPost(input: PostInput): Promise<SavePostResult> {
  return api<SavePostResult>('/api/publishing/posts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updatePost(id: string, input: PostInput): Promise<SavePostResult> {
  return api<SavePostResult>(`/api/publishing/posts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function deletePost(id: string): Promise<void> {
  await api<{ ok: true }>(`/api/publishing/posts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---- multi-account fan-out ----

export interface MultiPostInput {
  accountIds: string[];
  content: string;
  media: PostMedia[];
  targets: PostInput['targets'];
  scheduledFor?: string | null;
  publishMode: 'draft' | 'schedule' | 'now';
}

export type { FanOutResultRow };
export type FanOutResponse = FanOutSummary & { batchId: string };

export async function fanOutPost(input: MultiPostInput): Promise<FanOutResponse> {
  return api<FanOutResponse>('/api/publishing/posts/fan-out', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function validatePost(input: PostInput): Promise<ValidateIssue[]> {
  const { issues } = await api<{ issues: ValidateIssue[] }>('/api/publishing/posts/validate', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return issues;
}

// ---- media ----

export async function addMediaFromDrive(driveFileId: string): Promise<PostMedia> {
  const { media } = await api<{ media: PostMedia }>('/api/publishing/media/from-drive', {
    method: 'POST',
    body: JSON.stringify({ driveFileId }),
  });
  return media;
}

// ---- native upload: presign, then PUT DIRECT FROM THE BROWSER ----
//
// ⚠️⚠️ THE BYTES MUST NOT GO THROUGH OUR SERVER. Cloud Run rejects an HTTP/1
// request body over 32 MiB before Express ever sees it, which is why the old
// proxy route 413'd on any real video (15 MB worked, 33 MB didn't) — and it also
// meant buffering a whole file in a 1 GiB container. So the server only hands
// back a presigned slot and the browser uploads straight to Zernio's storage,
// which takes up to 5 GB.
//
// XHR, not fetch: fetch has no upload-progress event, and a 200 MB upload with
// no feedback is indistinguishable from a hung app.

export interface MediaPresign {
  uploadUrl: string;
  publicUrl: string;
  key: string;
  expiresIn: number;
}

export interface UploadProgress {
  loadedBytes: number;
  totalBytes: number;
  /** 0-100, or null while the browser hasn't reported a total yet. */
  percent: number | null;
}

export async function presignMediaUpload(file: File): Promise<MediaPresign> {
  return api<MediaPresign>('/api/publishing/media/presign', {
    method: 'POST',
    body: JSON.stringify({
      filename: file.name || 'upload',
      contentType: file.type,
      size: file.size,
    }),
  });
}

// A PUT that reports progress. Resolves with the HTTP status so the caller can
// tell an expired signature from a hard rejection.
function putWithProgress(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<{ status: number; body: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    // The presigned signature can be bound to the content type, so echo it exactly.
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      onProgress?.({
        loadedBytes: e.loaded,
        totalBytes: e.lengthComputable ? e.total : file.size,
        percent: e.lengthComputable && e.total > 0 ? Math.round((e.loaded / e.total) * 100) : null,
      });
    };
    xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText ?? '' });
    // ⚠️ A CORS rejection and a dropped connection are INDISTINGUISHABLE here —
    // the browser reports both as an error with status 0 and tells the page
    // nothing more (deliberately, so a page can't probe cross-origin state). The
    // message has to cover both honestly rather than guess.
    xhr.onerror = () =>
      reject(
        new Error(
          'The upload could not reach storage — check your connection and try again. ' +
            'If it keeps failing on every file, the storage host may be refusing browser uploads.',
        ),
      );
    xhr.ontimeout = () => reject(new Error('The upload timed out. Try again.'));
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}

// A presigned URL lasts an hour. A very large file on a slow connection can
// outlive it, and the storage rejects the PUT with a 403 — so re-presign and
// try once more rather than failing outright. Once only: if the upload genuinely
// takes longer than an hour, retrying forever just burns bandwidth.
function isExpiredSignature(status: number): boolean {
  return status === 400 || status === 401 || status === 403;
}

export async function uploadMedia(
  file: File,
  opts: { onProgress?: (p: UploadProgress) => void; signal?: AbortSignal } = {},
): Promise<PostMedia> {
  // Pre-validate BEFORE any network call, using the same rules the server
  // applies, so an unsupported file fails immediately and legibly.
  const check = validateMediaFile({ mimeType: file.type, sizeBytes: file.size });
  if (!check.ok) throw new Error(check.message);
  const { contentType } = check;

  let presign = await presignMediaUpload(file);
  let result = await putWithProgress(
    presign.uploadUrl,
    file,
    contentType,
    opts.onProgress,
    opts.signal,
  );

  if (!ok(result.status) && isExpiredSignature(result.status)) {
    presign = await presignMediaUpload(file);
    result = await putWithProgress(
      presign.uploadUrl,
      file,
      contentType,
      opts.onProgress,
      opts.signal,
    );
    if (!ok(result.status) && isExpiredSignature(result.status)) {
      throw new Error(
        `Storage rejected the upload twice (${result.status}). ` +
          'A very large file on a slow connection can outrun the one-hour upload window — ' +
          'try a smaller or more compressed file.',
      );
    }
  }
  if (!ok(result.status)) {
    throw new Error(`Storage rejected the upload (${result.status}). Try again.`);
  }

  return buildUploadedMedia({
    publicUrl: presign.publicUrl,
    filename: file.name || 'upload',
    contentType,
    sizeBytes: file.size,
    source: 'upload',
  });
}

function ok(status: number): boolean {
  return status >= 200 && status < 300;
}

// ---- queue ----

export async function listQueues(profileId: string): Promise<PostQueue[]> {
  const { queues } = await api<{ queues: PostQueue[] }>(
    `/api/publishing/queues?profileId=${encodeURIComponent(profileId)}`,
  );
  return queues;
}

export async function createQueue(input: {
  profileId: string;
  name: string;
  timezone: string;
  slots: PostQueueSlot[];
  active?: boolean;
}): Promise<PostQueue> {
  const { queue } = await api<{ queue: PostQueue }>('/api/publishing/queues', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return queue;
}

export async function updateQueue(input: {
  profileId: string;
  queueId?: string;
  name?: string;
  timezone: string;
  slots: PostQueueSlot[];
  active?: boolean;
  setAsDefault?: boolean;
  reshuffleExisting?: boolean;
}): Promise<PostQueue> {
  const { queue } = await api<{ queue: PostQueue }>('/api/publishing/queues', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return queue;
}

export async function deleteQueue(profileId: string, queueId: string): Promise<void> {
  await api<{ ok: true }>(
    `/api/publishing/queues?profileId=${encodeURIComponent(profileId)}&queueId=${encodeURIComponent(queueId)}`,
    { method: 'DELETE' },
  );
}

export async function getNextQueueSlot(profileId: string, queueId?: string): Promise<string | null> {
  const qs = new URLSearchParams({ profileId });
  if (queueId) qs.set('queueId', queueId);
  const { nextSlot } = await api<{ nextSlot: string | null }>(`/api/publishing/queues/next-slot?${qs}`);
  return nextSlot;
}

export async function previewQueue(profileId: string, queueId?: string, count = 5): Promise<string[]> {
  const qs = new URLSearchParams({ profileId, count: String(count) });
  if (queueId) qs.set('queueId', queueId);
  const { slots } = await api<{ slots: string[] }>(`/api/publishing/queues/preview?${qs}`);
  return slots;
}
